/* eslint-disable camelcase, jsx-a11y/no-noninteractive-element-interactions */
import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import {
  Table,
  Button,
  Glyphicon,
  ButtonToolbar,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import moment from 'moment-timezone';

import TimesheetEditorModalEnhanced from '../containers/TimesheetEditorModalEnhanced';
import NewTabLink from '../../../components/NewTabLink';
import { getTicketLink } from '../../ticketViewer/links';
import './TimesheetDetails.css';
import { openInNewTab } from '../../../utils';

const tdStyle = {
  padding: '0 8px',
};

const UNAVAILABLE = 'unavailable';
const REPAIRSMAINTENANCE = 'Repairs And Maintenance';

const ACTIVESTYLE = {
  zIndex: 2,
  color: '#fff',
  backgroundColor: '#337ab7',
  borderColor: '#337ab7',
};

const ODDROWCOLOR = '#FFFFFF';
const EVENROWCOLOR = '#BFBFBF';

const propTypes = exact({
  timeEntries: PropTypes.array.isRequired,
  dispatchShowCreateEditTimeEntryEditorModal: PropTypes.func.isRequired,
  dispatchShowDeleteTimeEntryEditorModal: PropTypes.func.isRequired,

  /* eslint-disable react/no-unused-prop-types */
  /* Query variable props */
  userId: PropTypes.number.isRequired,
  originDate: PropTypes.string.isRequired,
  timeEntryDate: PropTypes.string,
  /* eslint-enable react/no-unused-prop-types */

  timesheet: PropTypes.object.isRequired,
  weekStartDate: PropTypes.string.isRequired,
  weekEndDate: PropTypes.string.isRequired,
});

const getTicketState = (ticketNumber) => {
  if (!ticketNumber) {
    return null;
  }
  if (!ticketNumber.ticket) {
    return 'unavailable';
  }
  return ticketNumber.ticket.state;
};

/* TODO - This class is used in multiple contexts
 * (ie timesheetApprovals and TimesheeteNavigator)
 * however it is not designed for easy reuse across contexts
 * due to the way it is tied in with redux state.
 * It needs to be re-architected to support reuse
 * without the dependencies on redux state.
 */
const TimesheetDetails = (props) => {
  const {
    timeEntries,
    dispatchShowCreateEditTimeEntryEditorModal,
    dispatchShowDeleteTimeEntryEditorModal,
    weekStartDate,
    weekEndDate,
    timesheet,
    userId,
    originDate,
    timeEntryDate,
  } = props;

  const showActions = timesheet != null && timesheet.state === 'unapproved';

  let currentRow = null;
  let currentRowColor = null;

  const toggleColorRow = (timeEntry) => {
    if (!currentRow) {
      currentRowColor = ODDROWCOLOR;
    } else if (currentRow.date.iso8601 !== timeEntry.date.iso8601) {
      currentRowColor = (currentRowColor === EVENROWCOLOR) ? ODDROWCOLOR : EVENROWCOLOR;
    }
    currentRow = timeEntry;
    return currentRowColor;
  };

  const tooltip = (notes) => {
    return (
      <Tooltip id="tooltip" placement="top">
        {notes}
        {/* <strong>This ticket can not be changed.</strong> Ticket has already been processed. */}
      </Tooltip>
    );
  };

  const renderTimeEntryRows = (timeEntry) => {
    const {
      id, date, total_hours, activity_display, ticket_number, pto, ptoType,
    } = timeEntry;

    const { start_time: startTime, end_time: endTime, notes, paidPerDiem } = timeEntry;

    const department = (timeEntry.department) ? timeEntry.department.name : '';
    const district = (timeEntry.district) ? timeEntry.district.name : '';
    const ticketNumber = (ticket_number) ? ticket_number.number : '';
    const ticketState = getTicketState(ticket_number);
    const dateValue = moment(date.iso8601).format('MM/DD');
    const equipment = (timeEntry.equipment) ? timeEntry.equipment.equipment_number : '';
    const isActive = (timeEntryDate === moment(date.iso8601).format('YYYY-MM-DD'));
    const isNotes = (notes);

    const handleShowCreateEditModal = () => {
      dispatchShowCreateEditTimeEntryEditorModal(timeEntry, weekStartDate, weekEndDate);
    };

    const handleShowDeleteModal = () => {
      dispatchShowDeleteTimeEntryEditorModal(timeEntry);
    };

    const submissionItemId = timeEntry.timesheet.document.submission_item.id;

    const handleActivityDisplay = () => {
      if (activity_display === REPAIRSMAINTENANCE) {
        return 'R&M';
      }
      if (pto) {
        return `PTO - ${ptoType}`;
      }
      return activity_display;
    };

    const renderTicketNumber = () => {
      const isClickable = (ticketNumber && ticketState !== UNAVAILABLE);

      if (isClickable) {
        let ticketId = '';
        let jobId = '';
        if (ticket_number && ticket_number.ticket) {
          ticketId = ticket_number.ticket.id;
          jobId = ticket_number.ticket.job.id;
        }
        const url = getTicketLink(ticketId, jobId);
        return (
          <NewTabLink to={url}>
            {ticketNumber}
          </NewTabLink>
        );
      }

      return (ticketNumber);
    };

    const handleShowSubmissionItem = (paramUserId, weekOfDate) => {
      const baseUrl = '/submissions/timesheets/details/';
      const timesheetSubmissionUrl = `${baseUrl}${submissionItemId}?userId=${paramUserId}&date=${weekOfDate}`;
      openInNewTab(timesheetSubmissionUrl);
    };

    const rowColor = toggleColorRow(timeEntry);
    const rowStyle = {
      backgroundColor: rowColor,
    };

    /* NOTE: start/end time for time entry is in UTC */
    const { timezone } = timeEntry;
    const tzFormatStartTime = (moment.tz(startTime.iso8601, timezone)).format('HH:mm');
    const tzFormatEndTime = moment.tz(endTime.iso8601, timezone).format('HH:mm');

    return (
      <tr key={id} style={isActive ? ACTIVESTYLE : rowStyle}>
        <td style={tdStyle}>{date.day_of_week}</td>
        <td style={tdStyle}>{dateValue}</td>
        <td style={tdStyle}>{tzFormatStartTime}</td>
        <td style={tdStyle}>{tzFormatEndTime}</td>
        <td style={tdStyle}>{total_hours.to_string}</td>

        <td style={tdStyle}>{equipment}</td>
        <td style={tdStyle}>{handleActivityDisplay()}</td>
        <td style={tdStyle}>{department}</td>
        <td style={tdStyle}>{district}</td>
        <td style={tdStyle}>
          {isNotes && (
            <OverlayTrigger placement="top" overlay={tooltip(timeEntry.notes)}>
              <Glyphicon glyph="comment" />
            </OverlayTrigger>
          )}
          {paidPerDiem && (
            <OverlayTrigger placement="top" overlay={tooltip('Per Diem Paid')}>
              <Glyphicon glyph="usd" />
            </OverlayTrigger>
          )}
        </td>
        <td
          style={{ padding: '8px 8px', color: '#000' }}
          className={`summary-details-${ticketState}`}
        >
          {renderTicketNumber()}
        </td>
        <td style={tdStyle}>
          <ButtonToolbar name="changeTimeEntryButtons">
            <Button
              bsStyle="info"
              bsSize="xsmall"
              onClick={() => { return handleShowSubmissionItem(userId, date.iso8601); }}
            >
              <Glyphicon glyph="sunglasses" />
            </Button>
            {showActions && (
              <ButtonToolbar>
                <Button
                  bsStyle="warning"
                  bsSize="xsmall"
                  onClick={handleShowCreateEditModal}
                >
                  <Glyphicon glyph="pencil" />
                </Button>
                <Button
                  bsStyle="danger"
                  bsSize="xsmall"
                  onClick={handleShowDeleteModal}
                >
                  <Glyphicon glyph="minus" />
                </Button>
              </ButtonToolbar>
            )}
          </ButtonToolbar>
        </td>
      </tr>
    );
  };

  const handleCreateTimeEntryModal = () => {
    return dispatchShowCreateEditTimeEntryEditorModal(null, weekStartDate, weekEndDate);
  };

  return (
    <div>
      <div className="panel panel-default">
        <div className="panel-heading">
          Details
          {showActions && (
            <ButtonToolbar name="createTimeEntryButton" bsClass="pull-right">
              <Button bsStyle="primary" bsSize="xsmall" onClick={handleCreateTimeEntryModal}>
                <Glyphicon glyph="plus" />
                Add Entry
              </Button>
            </ButtonToolbar>
          )}
        </div>
        <div className="panel-body">
          <Row>
            <Col md={12}>
              <Table responsive>
                <thead>
                  <tr>
                    <th />
                    <th>Date</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Total</th>
                    <th>Equip.</th>
                    <th>Activity</th>
                    <th>Dept.</th>
                    <th>District</th>
                    <th>Notes</th>
                    <th>Ticket #</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  { timeEntries.map(renderTimeEntryRows, this) }
                </tbody>
              </Table>
            </Col>
          </Row>
        </div>
      </div>

      {showActions && <TimesheetEditorModalEnhanced userId={userId} originDate={originDate} />}
    </div>
  );
};

TimesheetDetails.propTypes = propTypes;

export default TimesheetDetails;
